import {
  FrameBundlePlatform,
  FSFBInputType,
  FrameBundleRootProto,
  ViewStateProto,
  Flags2,
} from '@fullstory/mobile-flatbuffers';
import { SelectorPart } from '@fullstory/proto/fs/selectors';
import { newSelectorPart } from '@fullstory/selectors';

const KEY_TAGNAME = 'tagName';
const KEY_TAG = 'tag';
const KEY_ID = 'id';
const KEY_CLASSNAME = 'className';
const KEY_PACKAGE = 'package';
const KEY_HREF = 'href';
const KEY_TYPE = 'type';

function* decodeVarints(a: Int8Array) {
  if (!a) {
    return;
  }

  let i = 0;

  outer: while (true) {
    let result = 0;
    let shift = 0;

    while (i < a.length) {
      const b = a[i++];

      result |= (b & 0x7f) << shift;
      shift += 7;

      // Is there more?
      if ((b & 0x80) == 0) {
        yield result;
        continue outer;
      }

      // Bad varint, return
      if (shift > 7 * 5) {
        return;
      }
    }

    break;
  }
}

export class SelectorFactory {
  private platform: FrameBundlePlatform;

  public constructor(private frameRoot: FrameBundleRootProto) {
    this.platform = frameRoot.platform() || FrameBundlePlatform.Android;
  }

  walkAttributes(state: ViewStateProto): Array<[number, number]> {
    const f: Int8Array = this.frameRoot.attributesArray();
    const v = decodeVarints(state.customAttrsArray());

    const r = [];

    for (const i in decodeVarints(f)) {
      const vn = v.next();
      if (vn.done) {
        break;
      }

      r.push([i, vn.value]);
    }

    while (true) {
      const vnk = v.next();
      if (vnk.done) {
        break;
      }
      const vnv = v.next();
      if (vnv.done) {
        r.push([vnk.value, null]);
        break;
      }
      r.push([vnk.value, vnv.value]);
    }

    return r;
  }

  lookupString(index: number): string {
    if (index == 0 || index > this.frameRoot.stringTableLength()) {
      return null;
    }
    const s = this.frameRoot.stringTable(index - 1);
    return s || null;
  }

  insertAttr(map: any, key: string, value: string) {
    let list = map[key];
    if (!list) {
      if (value) {
        map[key] = list = [value];
      } else {
        map[key] = [];
      }
    } else if (value) {
      list.push(value);
    }
  }

  setAttr(map: any, key: string, value: string) {
    if (value) {
      map[key] = [value];
    } else {
      map[key] = [];
    }
  }

  getAttr(map: any, key: string): string {
    const list = map[key];
    delete map[key];
    if (!list || list.length == 0) {
      return null;
    }

    list.sort();
    return list[0];
  }

  /**
   * Computes a selector from a view's state.
   */
  public compute(state: ViewStateProto, root: boolean): SelectorPart | null {
    if (!state) {
      return null;
    }

    if (state.flags2() & (1 << Flags2.SELECTOR_IS_HIDDEN)) {
      return null;
    }

    const attrs = {};
    const tag = state.viewTag();
    if (tag != 0) {
      if (this.platform == FrameBundlePlatform.IOS) {
        // On iOS it gets converted to a hex number
        this.insertAttr(attrs, KEY_TAG, tag.toString(16));
      } else {
        const tagStr = this.lookupString(tag);
        if (tagStr) {
          this.insertAttr(attrs, KEY_TAG, this.lookupString(tag));
        }
      }
    }

    let typeFromInputType = false;
    if (this.platform == FrameBundlePlatform.Android) {
      const inputType = (state.inputType() >> 24) & 0xff;
      const type = inputType && (FSFBInputType[inputType] || null);
      if (type) {
        this.insertAttr(attrs, KEY_TYPE, type.toLowerCase());
        typeFromInputType = true;
      }
    }

    const url = state.url();
    if (url) {
      this.insertAttr(attrs, KEY_HREF, this.lookupString(url));
    }

    const id = state.viewId();
    if (id) {
      this.insertAttr(attrs, KEY_ID, this.lookupString(id));
    }

    if (state.customAttrsLength() > 0) {
      this.walkAttributes(state).forEach(e => {
        const val = e[1] ? this.lookupString(e[1]) : '';
        if (val != '(masked)') {
          const key = this.lookupString(e[0]);
          // Generally if we get multiple values for a key, we want to join them.
          // But type is special, because we set the default value from inputType (see above) - so if there is a new value here, we should overwrite rather than append.
          if (typeFromInputType && key == 'type') {
            this.setAttr(attrs, key, val);
            typeFromInputType = false;
          } else {
            this.insertAttr(attrs, key, val);
          }
        }
      });
    } else {
      const clazz = this.lookupString(state.viewClass()) || '';
      if (clazz) {
        const periodIndex = clazz.lastIndexOf('.');
        if (periodIndex != -1) {
          this.insertAttr(attrs, KEY_TAGNAME, clazz.slice(periodIndex + 1));
          this.insertAttr(attrs, KEY_PACKAGE, clazz.slice(0, periodIndex));
        } else {
          this.insertAttr(attrs, KEY_TAGNAME, clazz);
        }
      }
    }
    const rule = newSelectorPart();

    rule.tag = this.getAttr(attrs, KEY_TAGNAME) || (root ? 'Root' : 'View');
    rule.id = this.getAttr(attrs, KEY_ID);

    if (attrs[KEY_CLASSNAME]) {
      const allClasses = {};
      rule.classes = [];
      attrs[KEY_CLASSNAME].filter(p => !!p).forEach(element => {
        if (!allClasses[element]) {
          allClasses[element] = true;
          rule.classes.push(element);
        }
      });
      delete attrs[KEY_CLASSNAME];
    }

    Object.keys(attrs).forEach(key => {
      const a = attrs[key].filter(p => !!p);
      if (a.length == 0) {
        rule.attrs[key] = '';
        rule.attrMatch[key] = '';
      } else {
        rule.attrs[key] = a.join(' ');
        rule.attrMatch[key] = '=';
      }
    });

    return rule;
  }
}
